/**
 * RolesPage — Settings → Permissions & Roles (RBAC v2 Sprint 2).
 *
 * Lists every role visible in the active group: system roles (read-only),
 * shipped templates (read-only — clone to edit), and tenant-created custom
 * roles. Provides clone-from-template, Create Role, and inline navigation
 * to RoleDetailPage.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  ContentCopy as CloneIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { adminRolesApi, RoleSummary } from '@/api/endpoints/adminRoles';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import { cleanGridSx, tableCardSx } from '@/theme/settingsTableStyles';

interface Props {
  embedded?: boolean;
}

export default function RolesPage({ embedded }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotificationStore((s) => s.show);
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('settings.roles.write');

  // createOpen = true means user clicked "Create Role" (source chosen from dropdown)
  // cloneOpen = true means user clicked per-row "Clone" (source pre-filled)
  const [createOpen, setCreateOpen] = useState(false);
  const [createSourceId, setCreateSourceId] = useState<string>('');
  const [createName, setCreateName] = useState('');

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState<RoleSummary | null>(null);
  const [cloneName, setCloneName] = useState('');

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: async () => (await adminRolesApi.list()).data,
  });

  const cloneMutation = useMutation({
    mutationFn: async () => {
      if (!cloneSource) throw new Error('No source role selected');
      const res = await adminRolesApi.clone({
        source_role_id: cloneSource.id,
        name: cloneName.trim(),
      });
      return res.data;
    },
    onSuccess: (newRole) => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      setCloneOpen(false);
      setCloneSource(null);
      setCloneName('');
      notify(`Role "${newRole.name}" created`, 'success');
      navigate(`/settings/roles/${newRole.id}`);
    },
    onError: (err: any) => {
      notify(err?.response?.data?.detail ?? 'Failed to clone role', 'error');
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!createSourceId) throw new Error('Please select a starting template');
      const res = await adminRolesApi.clone({
        source_role_id: createSourceId,
        name: createName.trim(),
      });
      return res.data;
    },
    onSuccess: (newRole) => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      setCreateOpen(false);
      setCreateSourceId('');
      setCreateName('');
      notify(`Role "${newRole.name}" created`, 'success');
      navigate(`/settings/roles/${newRole.id}`);
    },
    onError: (err: any) => {
      notify(err?.response?.data?.detail ?? 'Failed to create role', 'error');
    },
  });

  // Roles available as "starting point" for Create Role (exclude tier-locked ones)
  const createSourceOptions = roles.filter((r) => !r.tier_locked);

  const columns: GridColDef<RoleSummary>[] = [
    {
      field: 'name',
      headerName: 'Role',
      flex: 1.5,
      minWidth: 240,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.row.name}
          </Typography>
          {params.row.is_system && <Chip label="System" size="small" color="default" />}
          {params.row.is_template && !params.row.is_system && (
            <Chip label="Template" size="small" color="primary" variant="outlined" />
          )}
          {params.row.requires_2fa && (
            <Tooltip title="2FA required">
              <SecurityIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
          {params.row.is_external && (
            <Tooltip title="External role (time-bound)">
              <ScheduleIcon fontSize="small" color="info" />
            </Tooltip>
          )}
          {params.row.expires_at && (
            <Tooltip title={`Expires ${new Date(params.row.expires_at).toLocaleDateString()}`}>
              <Chip label="Time-bound" size="small" color="warning" variant="outlined" />
            </Tooltip>
          )}
          {params.row.tier_locked && (
            <Tooltip title="Upgrade your plan to use this template">
              <Chip
                icon={<UpgradeIcon />}
                label="Upgrade"
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          {params.row.description ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'member_count',
      headerName: 'Members',
      width: 110,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'permission_count',
      headerName: 'Permissions',
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const editable = canWrite && !params.row.is_template && !params.row.is_system;
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={editable ? 'Edit role' : 'View role'}>
              <Button
                size="small"
                startIcon={editable ? <EditIcon /> : <LockIcon />}
                onClick={() => navigate(`/settings/roles/${params.row.id}`)}
              >
                {editable ? 'Edit' : 'View'}
              </Button>
            </Tooltip>
            {canWrite && !params.row.tier_locked && (
              <Tooltip title="Clone from this role">
                <Button
                  size="small"
                  startIcon={<CloneIcon />}
                  onClick={() => {
                    setCloneSource(params.row);
                    setCloneName(`${params.row.name} (Copy)`);
                    setCloneOpen(true);
                  }}
                >
                  Clone
                </Button>
              </Tooltip>
            )}
            {canWrite && params.row.tier_locked && (
              <Tooltip title="Upgrade your plan to clone this template">
                <Button
                  size="small"
                  startIcon={<UpgradeIcon />}
                  color="secondary"
                  onClick={() => navigate('/settings#billing-plans')}
                >
                  Upgrade
                </Button>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Permissions & Roles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage who can do what. Create a custom role or clone a template; system roles
              cannot be edited.
            </Typography>
          </Box>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateName('');
                setCreateSourceId('');
                setCreateOpen(true);
              }}
              sx={{ flexShrink: 0, ml: 2 }}
            >
              Create Role
            </Button>
          )}
        </Stack>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Templates are starting points — clone one to a custom role to edit permissions, field
        access, and scopes. Changes propagate to assigned users within 60 seconds.
      </Alert>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={tableCardSx}>
          <DataGrid
            rows={roles}
            columns={columns}
            getRowId={(row) => row.id}
            autoHeight
            disableRowSelectionOnClick
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            getRowClassName={(params) => (params.row.tier_locked ? 'tier-locked-row' : '')}
            // Name cell stacks chips next to the label and description wraps to
            // two lines — auto row height stops them overlapping adjacent cells
            getRowHeight={() => 'auto'}
            sx={{
              ...cleanGridSx,
              '& .tier-locked-row': { opacity: 0.55 },
              '& .MuiDataGrid-cell': {
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                borderColor: 'divider',
                fontSize: 13.5,
              },
            }}
          />
        </Box>
      )}

      {/* Create Role dialog — user picks a starting template and sets a name */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <BrandedDialogTitle>Create custom role</BrandedDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Custom roles start as a copy of an existing role. Choose the closest match and
              then edit its permissions, field access, and scopes as needed.
            </Alert>
            <TextField
              label="Role name"
              fullWidth
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              autoFocus
              placeholder="e.g. Senior HR Manager"
            />
            <FormControl fullWidth>
              <InputLabel>Starting template</InputLabel>
              <Select
                value={createSourceId}
                label="Starting template"
                onChange={(e) => setCreateSourceId(e.target.value)}
              >
                <MenuItem value="" disabled>Select a role to copy from…</MenuItem>
                {createSourceOptions.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>{r.name}</Typography>
                      {r.description && (
                        <Typography variant="caption" color="text.secondary">{r.description}</Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!createName.trim() || !createSourceId || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} fullWidth maxWidth="sm">
        <BrandedDialogTitle>Clone role</BrandedDialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Cloning copies all permissions, field access, and scopes from{' '}
              <strong>{cloneSource?.name}</strong>. Later changes to the source do not propagate.
            </Alert>
            <TextField
              label="New role name"
              fullWidth
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!cloneName.trim() || cloneMutation.isPending}
            onClick={() => cloneMutation.mutate()}
          >
            {cloneMutation.isPending ? 'Cloning…' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
